import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Button, Chip, Paper, Alert, Breadcrumbs, Link, Divider, IconButton, Stack } from '@mui/material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaArrowLeft, FaChevronLeft, FaChevronRight, FaWhatsapp } from 'react-icons/fa';
import Loading from '../components/Loading';
import { productService } from '../services/api';

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    const resolveImage = (raw) => {
      if (!raw) return null;
      const cleanedRaw = String(raw).trim();
      // Accept full URLs and data URIs; trim to avoid leading/trailing spaces/newlines
      if (/^https?:\/\//i.test(cleanedRaw) || /^data:/i.test(cleanedRaw)) return cleanedRaw;
      const base = (process.env.REACT_APP_API_URL || 'http://localhost:8080').replace(/\/$/, '');
      const path = cleanedRaw.startsWith('/') ? cleanedRaw.slice(1) : cleanedRaw;
      // Heuristics: if no slash, assume filename under uploads/
      const hasSlash = /\//.test(path);
      const candidatePaths = [
        path,
        hasSlash ? path : `uploads/${path}`,
        `backend/${path}`,
      ];
      return `${base}/${candidatePaths[0]}`;
    };

    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await productService.getById(id);
        const p = response.data || {};
        const images = Array.isArray(p.images) ? p.images : (p.image ? [p.image] : []);
        const normImages = images
          .map(img => resolveImage(img))
          .filter(Boolean)
          .map(img => String(img).trim());
        const normImage = resolveImage(p.image);
        setProduct({ ...p, image: normImage || normImages[0] || null, images: normImages });
        setError(null);
      } catch (err) {
        setError('Impossible de charger les détails du produit.');
        console.error('Error fetching product:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  if (loading) return <Loading />;

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert 
          severity="error" 
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.15)',
          }}
        >
          {error}
        </Alert>
        <Button 
          startIcon={<FaArrowLeft />} 
          onClick={() => navigate('/')} 
          sx={{ 
            mt: 3,
            fontWeight: 600,
          }}
        >
          Retour à l'accueil
        </Button>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Produit introuvable
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Le produit que vous recherchez n'existe pas ou a été supprimé.
        </Typography>
        <Button 
          variant="contained"
          startIcon={<FaArrowLeft />} 
          onClick={() => navigate('/')}
        >
          Retour à l'accueil
        </Button>
      </Container>
    );
  }

  // Utiliser le tableau images du produit, ou l'image unique si images n'existe pas
  const productImages = product.images && product.images.length > 0 
    ? product.images 
    : (product.image ? [product.image] : []);

  const isAvailable = product.disponibilite === 1 || product.disponibilite === '1';

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Hero Section with Breadcrumbs */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0077B8 0%, #00ACEA 100%)',
          py: { xs: 3, md: 4 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(circle at 20% 50%, rgba(251, 191, 36, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 50%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)
            `,
            opacity: 0.5,
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Breadcrumbs 
            sx={{ 
              mb: 2,
              '& .MuiBreadcrumbs-separator': { 
                color: 'rgba(255, 255, 255, 0.6)',
              }
            }}
          >
            <Link 
              component={RouterLink} 
              to="/" 
              underline="hover" 
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.875rem',
                '&:hover': { color: '#fbbf24' }
              }}
            >
              Accueil
            </Link>
            <Link 
              component={RouterLink} 
              to="/categories" 
              underline="hover" 
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.875rem',
                '&:hover': { color: '#fbbf24' }
              }}
            >
              Catalogue
            </Link>
            <Typography 
              sx={{ 
                color: 'white',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {product.titre}
            </Typography>
          </Breadcrumbs>

          <Button 
            startIcon={<FaArrowLeft />} 
            onClick={() => navigate(-1)} 
            sx={{ 
              color: 'white',
              '&:hover': { 
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
              fontSize: '0.938rem',
              fontWeight: 600,
            }}
          >
            Retour
          </Button>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Image section centered at the top */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 4, md: 6 } }}>
          <Paper 
            elevation={0}
            sx={{ 
              borderRadius: 4,
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'white',
              width: '100%',
              maxWidth: 560,
            }}
          >
              {/* Status Badge */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 20,
                  right: 20,
                  zIndex: 10,
                }}
              >
                {isAvailable ? (
                  <Chip 
                    icon={<FaCheckCircle />} 
                    label="En Stock" 
                    sx={{ 
                      bgcolor: '#10b981',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
                      border: '2px solid white',
                    }}
                  />
                ) : (
                  <Chip 
                    icon={<FaTimesCircle />} 
                    label="Rupture de Stock" 
                    sx={{ 
                      bgcolor: '#ef4444',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
                      border: '2px solid white',
                    }}
                  />
                )}
              </Box>

              {/* Product Image with simple swipe controls (like catalogue) */}
              <Box sx={{ p: { xs: 2, md: 4 } }}>
                <Box
                  sx={{
                    width: '100%',
                    height: 0,
                    paddingBottom: '100%',
                    position: 'relative',
                    bgcolor: '#f8fafc',
                    overflow: 'hidden',
                    borderRadius: 3,
                  }}
                >
                  {/* Prev/Next buttons when multiple images */}
                  {productImages.length > 1 && (
                    <>
                      <IconButton
                        onClick={() => setSelectedImageIndex((prev) => (prev === 0 ? productImages.length - 1 : prev - 1))}
                        size="small"
                        sx={{
                          position: 'absolute',
                          left: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          bgcolor: 'rgba(255, 255, 255, 0.95)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          width: 32,
                          height: 32,
                          zIndex: 2,
                          '&:hover': { bgcolor: '#0077B8', color: 'white' },
                        }}
                      >
                        <FaChevronLeft size={14} />
                      </IconButton>
                      <IconButton
                        onClick={() => setSelectedImageIndex((prev) => (prev === productImages.length - 1 ? 0 : prev + 1))}
                        size="small"
                        sx={{
                          position: 'absolute',
                          right: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          bgcolor: 'rgba(255, 255, 255, 0.95)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          width: 32,
                          height: 32,
                          zIndex: 2,
                          '&:hover': { bgcolor: '#0077B8', color: 'white' },
                        }}
                      >
                        <FaChevronRight size={14} />
                      </IconButton>
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          bgcolor: 'rgba(0, 119, 184, 0.9)',
                          color: 'white',
                          px: 1,
                          py: 0.5,
                          borderRadius: '12px',
                          fontSize: '0.688rem',
                          fontWeight: 700,
                          zIndex: 2,
                        }}
                      >
                        {selectedImageIndex + 1}/{productImages.length}
                      </Box>
                    </>
                  )}
                  <Box
                    component="img"
                    src={(productImages[selectedImageIndex] || 'https://via.placeholder.com/800x600?text=Image+non+disponible')}
                    alt={product.titre}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      maxWidth: '90%',
                      maxHeight: '90%',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      display: 'block',
                    }}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/800x600?text=Image+non+disponible';
                    }}
                  />
                </Box>
              </Box>

              {/* Trust badges removed as requested */}
          </Paper>
        </Box>

        {/* Details below the image section */}
        <Box sx={{ maxWidth: 920, mx: 'auto' }}>
          <Box>
              {/* Category Badge */}
              <Chip 
                label={product.categorie_titre || 'Produit'} 
                sx={{ 
                  mb: 2,
                  bgcolor: '#0077B8',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  px: 1,
                }}
              />

              {/* Product Title */}
              <Typography 
                variant="h3" 
                component="h1"
                sx={{ 
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                  fontWeight: 900,
                  color: '#0c4a6e',
                  mb: 2,
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                }}
              >
                {product.titre}
              </Typography>
              <Divider sx={{ my: 3 }} />

              {/* Description */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h6"
                  fontWeight={700}
                  sx={{ 
                    mb: 2,
                    fontSize: '1.125rem',
                    color: '#0c4a6e',
                  }}
                >
                  Détails du produit
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    lineHeight: 1.8,
                    whiteSpace: 'pre-line',
                    color: '#475569',
                    fontSize: '1rem',
                  }}
                >
                  {product.description || 'Aucune description disponible pour ce produit.'}
                </Typography>
              </Box>

              {/* CTA Section (restored) */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: '2px solid',
                  borderColor: isAvailable ? '#10b981' : '#ef4444',
                  bgcolor: isAvailable ? 'rgba(16, 185, 129, 0.05)' : 'rgba(239, 68, 68, 0.05)',
                }}
              >
                <Typography 
                  variant="h6" 
                  fontWeight={700}
                  sx={{ 
                    mb: 2,
                    color: '#0c4a6e',
                  }}
                >
                  {isAvailable ? 'Intéressé par ce produit ?' : 'Produit temporairement indisponible'}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ mb: 3, lineHeight: 1.6 }}
                >
                  Contactez notre équipe pour obtenir un devis personnalisé et des informations détaillées sur ce produit.
                </Typography>
                
                <Stack spacing={2}>
                  <Button 
                    variant="contained" 
                    size="large" 
                    fullWidth 
                    startIcon={<FaWhatsapp style={{ color: '#25D366' }} />}
                    onClick={() => {
                      const msg = encodeURIComponent(`Bonjour, je suis intéressé par le produit: ${product.titre}`);
                      window.open(`https://wa.me/212665049299?text=${msg}`, '_blank');
                    }}
                    sx={{ 
                      py: 1.75,
                      fontSize: '1rem',
                      fontWeight: 700,
                      borderRadius: 999,
                      textTransform: 'none',
                      color: '#ffffff',
                      background: 'linear-gradient(135deg, #2b7bd4 0%, #0077B8 100%)',
                      boxShadow: '0 6px 18px rgba(0, 119, 184, 0.35)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1f66b8 0%, #004F82 100%)',
                        boxShadow: '0 8px 22px rgba(0, 119, 184, 0.45)',
                      },
                    }}
                  >
                    Contacter via WhatsApp
                  </Button>
                </Stack>
              </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ProductDetails;
